import React, { useState, useEffect } from "react";
import api from "../../services/api";
import toast from "react-hot-toast";
import AdminLayout from "../layouts/AdminLayout";
import PageTitle from "../components/ui/PageTitle";
import { CheckCircle, Clock, XCircle, Ban } from "lucide-react";

export default function PaymentApprovals() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("Pending"); // Tabs: Pending, Approved, Rejected

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("admin_token");
      // Ab hum dynamic status bhej rahe hain backend ko
      const res = await api.get(`/utr-requests?status=${activeTab}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(res.data.data || []);
    } catch (error) {
      toast.error("Failed to fetch requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [activeTab]); // Jab bhi tab change hoga, data re-fetch hoga

  const handleAction = async (id, actionType) => {
    const isApprove = actionType === 'approve';
    const confirmMsg = isApprove 
      ? "Approve this payment and add 1000 coins?" 
      : "Reject this payment request?";
      
    if (!window.confirm(confirmMsg)) return;
    
    try {
      const token = localStorage.getItem("admin_token");
      const endpoint = isApprove ? `/approve-utr/${id}` : `/reject-utr/${id}`; // Ensure you have a reject route
      
      await api.post(endpoint, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(isApprove ? "Payment Approved! Coins added." : "Payment Rejected.");
      // Remove from current list
      setRequests(requests.filter(req => req.id !== id));
    } catch (error) {
      toast.error(error.response?.data?.message || `Failed to ${actionType} payment`);
    }
  };

  return (
    <AdminLayout>
      <PageTitle title="Manual Payment Approvals" subtitle="Verify and manage UTR numbers submitted via QR code." />

      {/* 🌟 NAYA: TABS UI */}
      <div className="flex gap-4 mt-6 border-b border-gray-200">
        {['Pending', 'Approved', 'Rejected'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`pb-3 px-4 font-bold text-sm transition-colors border-b-2 ${
              activeTab === tab 
                ? 'border-yellow-500 text-yellow-600' 
                : 'border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-300'
            }`}
          >
            {tab} Requests
          </button>
        ))}
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mt-4">
        {loading ? (
          <div className="flex justify-center p-8"><div className="animate-spin h-8 w-8 border-b-2 border-yellow-500 rounded-full"></div></div>
        ) : requests.length === 0 ? (
          <div className="text-center text-gray-500 py-12 flex flex-col items-center">
            <Clock size={40} className="text-gray-300 mb-3" />
            <p className="font-bold text-lg">No {activeTab} requests found.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider border-b border-gray-200">
                  <th className="p-4 font-bold">Candidate Name</th>
                  <th className="p-4 font-bold">UTR Number</th>
                  <th className="p-4 font-bold">Amount</th>
                  <th className="p-4 font-bold">Date</th>
                  <th className="p-4 font-bold text-right">Status / Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {requests.map((req) => (
                  <tr key={req.id} className="hover:bg-gray-50 transition-colors">
                    <td className="p-4 font-bold text-gray-900">{req.candidate?.name || "Unknown"}</td>
                    <td className="p-4 font-mono font-bold text-blue-600">{req.utr_number}</td>
                    <td className="p-4 font-semibold text-gray-700">₹{req.amount}</td>
                    <td className="p-4 text-sm text-gray-500">{new Date(req.created_at).toLocaleDateString()}</td>
                    <td className="p-4 text-right">
                      
                      {activeTab === 'Pending' ? (
                        <div className="flex justify-end gap-2">
                          <button 
                            onClick={() => handleAction(req.id, 'reject')}
                            className="flex items-center gap-1 bg-red-100 hover:bg-red-200 text-red-700 px-3 py-2 rounded-lg text-sm font-bold transition-colors"
                          >
                            <Ban size={16} /> Reject
                          </button>
                          <button 
                            onClick={() => handleAction(req.id, 'approve')}
                            className="flex items-center gap-1 bg-green-500 hover:bg-green-600 text-white px-3 py-2 rounded-lg text-sm font-bold shadow-sm transition-colors"
                          >
                            <CheckCircle size={16} /> Approve
                          </button>
                        </div>
                      ) : (
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider ${
                          activeTab === 'Approved' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {activeTab === 'Approved' ? <CheckCircle size={14} /> : <XCircle size={14} />}
                          {req.status}
                        </span>
                      )}

                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}